import { toSigFigs } from "../../../utils/format";
import type { LoadImpactSummary } from "../domain/types";
import { getCategoryDisplayName, isHiddenCategory } from "../../model/domain/definitions";

interface Props {
  summary: LoadImpactSummary;
}

// Maps emission pretty_name → { category, unit } matching EMISSIONS_MAP in mappings.py.
// Units are the converted (tonnes_unit) values since the backend applies the multiplier.
const EMISSION_META: Record<string, { category: string; unit: string }> = {
  // Air
  Carbon: { category: "Air", unit: "Tonnes CO₂-Eq" },
  "Human Toxicity (Cancerous)": { category: "Air", unit: "Tonnes 1,4-DCB-Eq" },
  "Human Toxicity (Non-Cancerous)": {
    category: "Air",
    unit: "Tonnes 1,4-DCB-Eq",
  },
  "Ionising Radiation": { category: "Air", unit: "MBq Co-60-Eq" },
  "Ozone Depletion": { category: "Air", unit: "Tonnes CFC-11-Eq" },
  "Particulate Matter": { category: "Air", unit: "Tonnes PM2.5-Eq" },
  "Photochemical Oxidants (Public Health)": {
    category: "Air",
    unit: "Tonnes NOx-Eq",
  },
  "Photochemical Oxidants (Ecosystems)": {
    category: "Air",
    unit: "Tonnes NOx-Eq",
  },
  // Soil
  "Terrestrial Acidification": { category: "Soil", unit: "Tonnes SO₂-Eq" },
  "Terrestrial Ecotoxicity": { category: "Soil", unit: "Tonnes 1,4-DCB-Eq" },
  // Water
  "Freshwater Ecotoxicity": { category: "Water", unit: "Tonnes 1,4-DCB-Eq" },
  "Marine Ecotoxicity": { category: "Water", unit: "Tonnes 1,4-DCB-Eq" },
  "Freshwater Eutrophication": { category: "Water", unit: "Tonnes P-Eq" },
  "Marine Eutrophication": { category: "Water", unit: "Tonnes N-Eq" },
  // Resources
  "Energy Resources: Non-Renewable, Fossil": {
    category: "Resources",
    unit: "Tonnes oil-Eq",
  },
  "Material Resources: Metals/Minerals": {
    category: "Resources",
    unit: "Tonnes Cu-Eq",
  },
  "Land Use": {
    category: "Resources",
    unit: "annual m² crop-Eq",
  },
  "Water Use": { category: "Resources", unit: "Cubic Meters" },
  "Water Depletion": { category: "Resources", unit: "Cubic Meters" },
};

const CATEGORY_ORDER = ["Air", "Soil", "Water", "Resources"];

interface EmissionRow {
  name: string;
  unit: string;
  consumption: number;
  offset: number;
  infra: number;
  net: number;
}

export function LoadEmissionsCard({ summary }: Props) {
  // Consumption emissions = grid imports + on-site NG; offset = avoided grid
  // (on-site RE); infra = RE lifecycle. Net = consumption − offset + infra.
  const gridEmissions = summary.grid_supply?.emissions || {};
  const ngEmissions = summary.ng_impacts?.emissions || {};
  const reEmissions = summary.re_benefits?.emissions || {};
  const infraEmissions = summary.re_infrastructure?.emissions || {};

  const consumptionEmissions: Record<string, number> = {};
  for (const src of [gridEmissions, ngEmissions]) {
    for (const [k, v] of Object.entries(src)) {
      consumptionEmissions[k] = (consumptionEmissions[k] || 0) + (v as number);
    }
  }

  // Only hide if there's neither consumption nor any RE offset to show.
  if (
    Object.keys(consumptionEmissions).length === 0 &&
    Object.keys(reEmissions).length === 0
  )
    return null;

  const hasOffset =
    Object.values(reEmissions).some((v) => Math.abs(v as number) > 0) ||
    Object.values(infraEmissions).some((v) => Math.abs(v as number) > 0);

  // Collect all emission names from every source (drop hidden categories)
  const allNames = new Set(
    [
      ...Object.keys(consumptionEmissions),
      ...Object.keys(reEmissions),
      ...Object.keys(infraEmissions),
    ].filter((n) => !isHiddenCategory(n)),
  );

  // Group by category
  const grouped: Record<string, EmissionRow[]> = {};
  for (const cat of CATEGORY_ORDER) grouped[cat] = [];
  const uncategorized: EmissionRow[] = [];

  for (const name of allNames) {
    const consumption = consumptionEmissions[name] || 0;
    const offset = (reEmissions[name] as number) || 0;
    const infra = (infraEmissions[name] as number) || 0;
    const meta = EMISSION_META[name];
    const row: EmissionRow = {
      name,
      unit: meta?.unit || "",
      consumption,
      offset,
      infra,
      net: consumption - offset + infra,
    };
    if (meta) {
      grouped[meta.category].push(row);
    } else {
      uncategorized.push(row);
    }
  }

  // Sort each group by consumption magnitude
  for (const cat of CATEGORY_ORDER) {
    grouped[cat].sort((a, b) => Math.abs(b.consumption) - Math.abs(a.consumption));
  }
  uncategorized.sort((a, b) => Math.abs(b.consumption) - Math.abs(a.consumption));

  const renderTable = (rows: EmissionRow[]) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-surface-600">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
              Consumption
            </th>
            {hasOffset && (
              <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                <span className="text-green-400/70">RE offset</span>
              </th>
            )}
            {hasOffset && (
              <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                RE infra
              </th>
            )}
            <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
              Net
            </th>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Unit
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <tr
              key={row.name}
              className="hover:bg-surface-600 transition-colors"
            >
              <td className="px-4 py-2.5 text-sm text-text-secondary">
                {getCategoryDisplayName(row.name)}
              </td>
              <td className="px-4 py-2.5 text-sm text-text-secondary text-right font-medium whitespace-nowrap">
                {toSigFigs(row.consumption)}
              </td>
              {hasOffset && (
                <td className="px-4 py-2.5 text-sm text-green-400/80 text-right font-medium whitespace-nowrap">
                  {row.offset !== 0 ? `−${toSigFigs(row.offset)}` : "—"}
                </td>
              )}
              {hasOffset && (
                <td className="px-4 py-2.5 text-sm text-text-muted text-right font-medium whitespace-nowrap">
                  {row.infra !== 0 ? `+${toSigFigs(row.infra)}` : "—"}
                </td>
              )}
              <td className="px-4 py-2.5 text-sm text-text-secondary text-right font-semibold whitespace-nowrap">
                {toSigFigs(row.net)}
              </td>
              <td className="px-4 py-2.5 text-sm text-text-muted whitespace-nowrap">
                {row.unit}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <h3 className="text-lg font-medium text-text-primary">
          Air, Soil, Water &amp; Resource Emissions
        </h3>
        <span className="text-xs text-text-muted bg-surface-600 px-2 py-0.5 rounded">
          Cradle-to-grave lifecycle
        </span>
      </div>

      <div className="px-6 pb-5 space-y-5">
        {CATEGORY_ORDER.map((cat) => {
          const rows = grouped[cat];
          if (rows.length === 0) return null;
          return (
            <div key={cat}>
              <h4 className="text-sm font-medium text-text-primary mb-2">
                {cat}
              </h4>
              {renderTable(rows)}
            </div>
          );
        })}

        {uncategorized.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-2">
              Other
            </h4>
            {renderTable(uncategorized)}
          </div>
        )}
      </div>
    </div>
  );
}
